import { LoadingState, QueryError } from '#/components/common/loading-state'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { BellRing, Hammer, Loader2, Play, Rocket } from 'lucide-react'
import { AppHeader } from '#/components/layout/app-header'
import { Button } from '#/components/ui/button'
import { BuildWith } from '#/components/byoa/build-with'
import { WorkflowPanel } from '#/components/serve/workflow-panel'
import { EscalationLadder, type LadderStep } from '#/components/simple/escalation-ladder'
import { PlainProgress } from '#/components/simple/plain-progress'
import { StepCard } from '#/components/simple/step-card'
import { RulesCardSimple } from '#/components/rules/rules-card-simple'
import { DecisionsCardSimple } from '#/components/decisions/decisions-card-simple'
import { TryOnPhone } from '#/components/simple/try-on-phone'
import { useCancelRun, useEscalateRun, usePreview, useRetryRun, useRunStream, useRunner, useStartRun } from '#/lib/api-runner'
import { ApiError } from '#/lib/api'
import { useInbox, useMarkSeen } from '#/lib/api-inbox'
import { InboxList } from '#/components/inbox/inbox-list'
import { PrimaryAction } from '#/components/common/primary-action'
import { PhaseTrail } from '#/components/serve/phase-trail'
import { SessionCard, runStatusLabel } from '#/components/serve/session-card'
import { ReviewPanel } from '#/components/serve/review-panel'
import { DecisionCardsPanel } from '#/components/serve/decision-cards'
import type { RunBackend } from '#/lib/runner/types'
import { m } from '#/paraglide/messages'

/**
 * "Bangunkan untuk saya" — the whole Runner in one mobile-first screen (PRD §5).
 * No jargon, no kanban, no phases: steps, proof, a link to try on the phone, and a way out when stuck.
 */
export const Route = createFileRoute('/workspace/$workspaceId/bangun')({
  validateSearch: z.object({ plan: z.string().optional() }),
  component: BangunView,
})

function BangunView() {
  const { workspaceId } = Route.useParams()
  const { plan: planParam } = Route.useSearch()
  const navigate = useNavigate()
  const { data, isLoading, refetch, isError, isFetching } = useRunner(workspaceId, planParam)
  const start = useStartRun(workspaceId, data?.planId ?? planParam ?? null)
  const cancel = useCancelRun(workspaceId)
  const retry = useRetryRun(workspaceId)
  const escalate = useEscalateRun(workspaceId)
  const preview = usePreview(workspaceId)
  const [notice, setNotice] = useState<string | null>(null)
  const [workflowActive, setWorkflowActive] = useState(false)
  const [explanation, setExplanation] = useState<string | null>(null)
  const [localChoice, setLocalChoice] = useState<{ workspaceId: string; machineId: string; backend: RunBackend } | null>(null)
  const localBackend = localChoice?.workspaceId === workspaceId && localChoice.machineId === data?.machine?.id ? localChoice.backend : undefined
  // Inspirasi F1: what waits for the user in THIS workspace, and "seen" on mount so done-not-seen resets
  const { data: inbox } = useInbox()
  const waiting = (inbox?.items ?? []).filter((i) => i.workspaceId === workspaceId && i.state === 'blocked')
  const markSeen = useMarkSeen()
  useEffect(() => {
    markSeen.mutate(workspaceId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId])

  const active = data?.activeRun ?? null
  const last = data?.lastRun ?? null
  const stuck = last?.status === 'escalated' ? last : null
  const { lines, status: streamed } = useRunStream(active?.id ?? stuck?.id ?? null)
  const working = Boolean(active) && streamed?.status !== 'passed'

  useEffect(() => {
    if (!streamed) return
    if (streamed.status === 'passed' || streamed.status === 'escalated' || streamed.status === 'failed') void refetch()
  }, [streamed, refetch])

  const runStart = () => {
    setNotice(null)
    start.mutate(data?.executor === 'local' && localBackend ? { backend: localBackend } : undefined, {
      onError: (e) => setNotice(e instanceof ApiError ? (e.code === 'runner_credits' ? m.rn_credits_out() : e.code === 'runner_paused' ? m.rn_paused() : e.message) : m.rn_error_generic()),
    })
  }
  const runEscalate = (step: LadderStep) => {
    if (!stuck) return
    escalate.mutate(
      { runId: stuck.id, step },
      {
        onSuccess: (r) => {
          if (r.plainText) setExplanation(r.plainText)
          void refetch()
        },
        onError: (e) => setNotice(e instanceof ApiError ? e.message : m.rn_error_generic()),
      },
    )
  }
  const goChange = () => navigate({ to: '/workspace/$workspaceId/$view', params: { workspaceId, view: 'roadmap' }, search: { plan: data?.planId ?? undefined } })

  const steps = data?.steps ?? []
  // Phase 2 link: the first stage is what the user promised themselves — once every step of it
  // passed, publishing is the next thing to do (docs/nontech/CONTRACT.md §Fase 2).
  const stageOne = steps.filter((s) => s.phase === 1)
  const readyToPublish = stageOne.length > 0 && stageOne.every((s) => s.status === 'done')
  const busy = workflowActive || start.isPending || cancel.isPending || retry.isPending || escalate.isPending

  return (
    <>
      <AppHeader showStats={false} compact />
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-4 p-4 pb-24 sm:p-6">
          <header>
            <h1 className="font-heading text-xl font-semibold">{m.rn_title()}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{m.rn_subtitle()}</p>
          </header>

          {isLoading && <LoadingState compact label={m.loading_board()} />}
          {isError && !data && <QueryError onRetry={refetch} retrying={isFetching} />}
          {!isLoading && !isError && !data?.planId && <p className="text-sm text-muted-foreground">{m.rn_no_plan()}</p>}
          {data && !data.enabled && <p className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">{m.rn_off()}</p>}
          {data?.paused && <p className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">{m.rn_paused()}</p>}
          {notice && <p className="rounded-lg border border-status-blocked/40 bg-status-blocked/5 p-3 text-sm">{notice}</p>}

          {waiting.length > 0 && (
            <section className="space-y-2 rounded-xl border border-status-progress/40 bg-status-progress/5 p-3" data-testid="bangun-needs-you">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-1.5 text-sm font-semibold"><BellRing className="size-4 text-status-progress" /> {m.inbox_panel_title()}</h2>
                <Link to="/inbox" className="text-xs text-primary hover:underline">{m.inbox_panel_more()}</Link>
              </div>
              <InboxList items={waiting.slice(0, 3)} compact />
            </section>
          )}

          {data?.planId && (
            <>
              {/* Inspirasi F2: the one big button whose label follows the state of the project */}
              <PrimaryAction planId={data.planId} mode="simple" size="lg" onFired={() => void refetch()} />
              <PlainProgress done={data.doneSteps} total={data.totalSteps} lines={lines} working={working} />
              {/* Parity P0/P6 — where this step is being built, and the four-phase trail (Ingat → Rencana → Kerjakan → Periksa) */}
              {(active || last) && (
                <div className="space-y-2 px-1" data-testid="bangun-run-meta">
                  {/* Orkestrator E1: the honest 3-slot label (where · which agent · who pays) replaces the source badge + model line */}
                  <SessionCard
                    as="div"
                    testId="bangun-run-card"
                    refCode={(active ?? last)!.taskRef ?? ''}
                    title={(active ?? last)!.plainTitle}
                    status={(active ?? last)!.status}
                    statusLabel={runStatusLabel((active ?? last)!.status)}
                    attempt={(active ?? last)!.attempt}
                    exec={{ location: (active ?? last)!.credentialSource === 'local' ? 'local' : 'cloud', agent: (active ?? last)!.backend, payer: (active ?? last)!.credentialSource, model: (active ?? last)!.model, machineName: data.executor === 'local' ? data.machine?.name ?? null : null }}
                    notes={lines.slice(-5).map((l) => ({ at: l.ts, text: l.plainText }))}
                    /* Orkestrator E2: the state the runtime itself reports + "Mengapa status ini?" */
                    stateRef={{ kind: 'run', runId: (active ?? last)!.id }}
                  />
                  <PhaseTrail lines={lines} run={streamed ?? active ?? null} done={!active && last?.status === 'passed'} />
                </div>
              )}

              {data.checkpoint && (
                <TryOnPhone
                  url={data.preview?.url ?? null}
                  status={data.preview?.status ?? 'none'}
                  plainDone={data.checkpoint.plainDone}
                  onWake={() => preview.mutateAsync()}
                  onContinue={runStart}
                  onChange={goChange}
                  onStop={() => active && cancel.mutate(active.id)}
                  busy={busy}
                />
              )}

              {stuck && <EscalationLadder currentStep={stuck.escalationStep} explanation={explanation} onEscalate={runEscalate} onRetry={() => retry.mutate(stuck.id)} busy={busy} />}

              <BuildWith workspaceId={workspaceId} aiConnectionId={data.aiConnectionId} backend={data.backend} backends={data.backends} executor={data.executor} machine={data.machine} localBackend={localBackend} onLocalBackendChange={(backend) => data.machine && setLocalChoice({ workspaceId, machineId: data.machine.id, backend })} onChanged={() => void refetch()} />
              {/* Orkestrator E2 §6 — findings of the Pemeriksa and the open decision cards; both render nothing until the server has them */}
              <ReviewPanel planId={data.planId} />
              <DecisionCardsPanel planId={data.planId} />
              <WorkflowPanel planId={data.planId} workspaceId={workspaceId} backend={localBackend ?? data.backend} executor={data.executor} machineName={data.executor === 'local' ? data.machine?.name ?? null : null} runActive={Boolean(active)} onActiveChange={setWorkflowActive} />

              <div className="flex flex-wrap items-center gap-2">
                {/* while a stage gate is open the buttons live inside "Coba dulu di HP-mu" — never offer two Lanjut */}
                {data.checkpoint ? null : active ? (
                  <Button loading={busy} variant="outline" size="sm" onClick={() => cancel.mutate(active.id)} disabled={busy}>
                    {m.rn_stop()}
                  </Button>
                ) : (
                  <Button size="sm" className="gap-1.5" onClick={runStart} disabled={busy || !data.enabled || data.paused || data.doneSteps >= data.totalSteps}>
                    {start.isPending ? <Loader2 className="size-4 animate-spin" /> : data.doneSteps > 0 ? <Play className="size-4" /> : <Hammer className="size-4" />}
                    {data.doneSteps > 0 ? m.rn_continue() : m.rn_start()}
                  </Button>
                )}
                <span className="text-xs text-muted-foreground">{data.credits.limit === -1 ? m.rn_credits_unlimited() : m.rn_credits({ n: data.credits.remaining })}</span>
                {data.credits.limit !== -1 && data.credits.remaining <= 0 && (
                  <Button asChild variant="link" size="sm" className="h-auto p-0 text-xs" data-testid="bangun-topup">
                    <Link to="/pricing">{m.rn_topup()}</Link>
                  </Button>
                )}
              </div>

              {data.doneSteps >= data.totalSteps && data.totalSteps > 0 && <p className="text-sm font-medium text-status-done">{m.rn_all_done()}</p>}

              {readyToPublish && (
                <Button asChild variant="secondary" size="sm" className="gap-1.5">
                  <Link to="/workspace/$workspaceId/terbitkan" params={{ workspaceId }}>
                    <Rocket className="size-4" /> {m.pb_goto()}
                  </Link>
                </Button>
              )}

              <ol className="space-y-2">
                {steps.map((s, i) => (
                  <StepCard
                    key={s.ref}
                    step={s}
                    index={i + 1}
                    indexOfRef={(ref) => {
                      const j = steps.findIndex((x) => x.ref === ref)
                      return j < 0 ? null : j + 1
                    }}
                  />
                ))}
              </ol>
              {/* inspirasi F3 — plain always / never / ask rules for the builder */}
              <RulesCardSimple workspaceId={workspaceId} />
              {/* Parity P3 — what the owner already decided, in plain words */}
              <DecisionsCardSimple workspaceId={workspaceId} />
            </>
          )}
        </div>
      </main>
    </>
  )
}
